import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { listManualLocks, addManualLock, deleteManualLock, listClasses, listSubjects, listStaff, getStaffBusyWeek, listTimetableRuns, getVanhoaDistribution, getPeriodNotes, setPeriodNote, TEACHER_DEPARTMENT } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";
import HappySchoolAllTeachersView from "./HappySchoolAllTeachersView";

// Đổi GV chính / thêm GV dự phòng cho 1 lớp-môn bất kỳ nằm ở tab riêng
// "Phân công chuyên môn" (HappySchoolPccmQuickEdit.js) — trang này chỉ còn
// đúng 1 việc: khoá tay từng ô trong lưới.

const DAYS = ["Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"];
// Giả định 2 buổi x 5 tiết (khớp DEFAULT_PERIOD_TIMES) — đúng số tiết dùng ở
// trang "TKB Giáo viên" thật, đủ để hiển thị lưới giống hệt.
const SESSIONS = [
  { label: "Sáng", periods: [0, 1, 2, 3, 4] },
  { label: "Chiều", periods: [5, 6, 7, 8, 9] },
];
function busyKeyToPairs(key) {
  const [dayStr, rest] = String(key).split("_");
  const day = Number(dayStr);
  if (Number.isNaN(day)) return [];
  if (rest === "S") return SESSIONS[0].periods.map((p) => [day, p]);
  if (rest === "C") return SESSIONS[1].periods.map((p) => [day, p]);
  const period = Number(rest);
  return Number.isNaN(period) ? [] : [[day, period]];
}

// "Case xếp tay" — TƯƠNG TÁC GIỐNG HỆT trang "Lịch bận": chọn 1 GV, bấm ô
// trong lưới để tích/bỏ NGAY TRONG MÀN HÌNH (chưa gọi API), rồi bấm 1 nút
// "Lưu thay đổi" để gửi hết 1 lượt — không còn kiểu bấm-1-ô-gọi-1-API như
// trước (hay bị "lúc được lúc không" vì phải chọn đúng Lớp/Môn trước mới
// bấm được). Đỏ/xám (GV báo bận / lớp nghỉ) chỉ là CẢNH BÁO, không chặn bấm
// — nếu CP-SAT không ép được thật thì tự hạ xuống mềm (hardLocksDowngraded).
// Nhóm con của "Case xếp tay" (cùng 1 kho dữ liệu khoá, chỉ lọc để dễ thao tác):
//  - "tg":    chỉ GV Thỉnh giảng
//  - "ttn12": chỉ lớp khối 12 + các môn thi tốt nghiệp (Toán, Văn, Sử, Địa, GDKT&PL, Công nghệ)
export const TTN12_SUBJECT_RE = /to[aá]n|ng[uữ] v[aă]n|l[iị]ch s[uử]|đ[iị]a l[iíý]|gdkt|kinh t[eế]|c[oô]ng ngh[eệ]/i;
const isTG = (st) => /th[iỉ]nh gi[aả]ng/i.test((st && st.employmentType) || "");
const isKhoi12 = (name) => /^\s*12/.test(name || "");

const HappySchoolManualLocks = ({ weekId, scope = "" }) => {
  const schoolId = getActiveSchoolId();
  const [locks, setLocks] = useState(null);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [busyWeek, setBusyWeek] = useState({});
  // Lịch phân phối nghề-văn hoá của tuần — CHỈ để hiện nhãn "Nghỉ"/"Học nghề"
  // tham khảo, KHÔNG chặn bấm: đúng yêu cầu "vẫn cho phép xếp đè lên" — ô
  // nghề/nghỉ vẫn khoá tay được bình thường (đã hỗ trợ ở solver từ trước).
  const [vanhoaRows, setVanhoaRows] = useState([]);
  // Ghi chú riêng theo ô (lớp-ngày-tiết), độc lập môn/GV/bản chạy — vd "cô X
  // trông lớp hộ". Shape: {classId: {"day_period": {note, updatedAt, updatedByUid}}}.
  const [notes, setNotes] = useState({});
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteTeacherId, setNoteTeacherId] = useState(""); // GV gắn với ghi chú (tuỳ chọn) — để hiện trên TKB của GV đó

  const [teacherId, setTeacherId] = useState("");
  const [classId, setClassId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [reason, setReason] = useState("");
  // "edit" = khoá/bỏ khoá ô (như cũ); "browse" = lướt TKB THẬT (từ bản mới
  // nhất) của riêng các GV đang có ít nhất 1 ô xếp tay tuần này — để soát lại
  // xem lịch xung quanh ô khoá có ổn không, không sửa gì ở đây.
  const [view, setView] = useState("edit");
  const [latestRunId, setLatestRunId] = useState(null);
  // day_period -> {classId, subjectId, reason} (thêm mới) | null (xoá cái đã
  // có) — CHỈ chứa ô đang có thay đổi CHƯA LƯU, giống hệt kiểu `drafts` ở
  // trang Lịch bận.
  const [pending, setPending] = useState({});

  const classesById = useMemo(() => Object.fromEntries(classes.map((c) => [c.id, c.name])), [classes]);
  const subjectsById = useMemo(() => Object.fromEntries(subjects.map((s) => [s.id, s.name])), [subjects]);
  const staffById = useMemo(() => Object.fromEntries(staff.map((s) => [s.id, s])), [staff]);
  const lockInScope = (l) => {
    if (scope === "tg") return isTG(staffById[l.teacherId]);
    if (scope === "ttn12") return isKhoi12(classesById[l.classId]) && TTN12_SUBJECT_RE.test(subjectsById[l.subjectId] || "");
    return true;
  };
  const scopedLocks = useMemo(() => (locks || []).filter(lockInScope), [locks, scope, staffById, classesById, subjectsById]); // eslint-disable-line
  const scopedClasses = useMemo(() => (scope === "ttn12" ? classes.filter((c) => isKhoi12(c.name)) : classes), [classes, scope]);
  const scopedSubjects = useMemo(() => (scope === "ttn12" ? subjects.filter((x) => TTN12_SUBJECT_RE.test(x.name || "")) : subjects), [subjects, scope]);
  const scopedStaff = useMemo(() => {
    if (scope === "tg") return staff.filter(isTG);
    if (scope === "ttn12") {
      const withLock = new Set(scopedLocks.map((l) => l.teacherId));
      return [...staff.filter((x) => withLock.has(x.id)), ...staff.filter((x) => !withLock.has(x.id))];
    }
    return staff;
  }, [staff, scope, scopedLocks]);
  useEffect(() => {
    if (scopedStaff.length && !scopedStaff.find((x) => x.id === teacherId)) setTeacherId(scopedStaff[0].id);
    // eslint-disable-next-line
  }, [scopedStaff]);

  const reload = () => {
    if (!schoolId || !weekId) return;
    setLoading(true);
    setError("");
    Promise.all([
      listManualLocks(schoolId, weekId),
      listClasses(schoolId),
      listSubjects(schoolId),
      listStaff(schoolId, { department: TEACHER_DEPARTMENT }),
      getStaffBusyWeek(schoolId, weekId),
      getVanhoaDistribution(schoolId, weekId),
      getPeriodNotes(schoolId, weekId),
    ])
      .then(([l, c, s, t, bw, vh, pn]) => {
        setLocks(l || []);
        setClasses((c || []).slice().sort((a, b) => a.name.localeCompare(b.name, "vi", { numeric: true })));
        setSubjects(s || []);
        const sortedStaff = (t || []).slice().sort((a, b) => a.name.localeCompare(b.name, "vi"));
        setStaff(sortedStaff);
        setBusyWeek(bw || {});
        setVanhoaRows((vh && vh.rows) || []);
        setNotes(pn || {});
        if (!teacherId && sortedStaff.length) setTeacherId(sortedStaff[0].id);
        setPending({});
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [schoolId, weekId]); // eslint-disable-line

  // Bản mới nhất của tuần này — dùng để lướt TKB thật ở tab "browse" (chỉ cần
  // Bản gần nhất, không cho chọn Bản khác — trang này không phải nơi so sánh
  // giữa các Bản, xem "TKB Giáo viên" ở tab riêng nếu cần).
  useEffect(() => {
    if (!schoolId || !weekId) { setLatestRunId(null); return; }
    listTimetableRuns(schoolId, weekId)
      .then((runs) => {
        const sorted = (runs || []).slice().sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setLatestRunId(sorted[0]?.id || null);
      })
      .catch(() => setLatestRunId(null));
  }, [schoolId, weekId]);

  const lockedTeacherIds = useMemo(() => new Set(scopedLocks.map((l) => l.teacherId)), [scopedLocks]);
  const lockedCellKeys = useMemo(
    () => new Set(scopedLocks.map((l) => `${l.teacherId}|${l.day}|${l.period}`)),
    [scopedLocks]
  );

  // Đổi GV -> Lớp/Môn tự chọn lại phần tử đầu tiên (KHÔNG để trống, tránh
  // phải tự tay chọn trước khi bấm được — luôn có sẵn 1 cặp lớp-môn hợp lệ).
  useEffect(() => {
    setPending({});
    if (scopedClasses.length && !scopedClasses.find((c) => c.id === classId)) setClassId(scopedClasses[0].id);
    if (scopedSubjects.length && !scopedSubjects.find((s) => s.id === subjectId)) setSubjectId(scopedSubjects[0].id);
    // eslint-disable-next-line
  }, [teacherId]);
  useEffect(() => {
    if (scopedClasses.length && !scopedClasses.find((c) => c.id === classId)) setClassId(scopedClasses[0].id);
    if (scopedSubjects.length && !scopedSubjects.find((s) => s.id === subjectId)) setSubjectId(scopedSubjects[0].id);
    // eslint-disable-next-line
  }, [scopedClasses, scopedSubjects]);

  const selectedTeacher = staff.find((s) => s.id === teacherId);

  // Ngày/buổi Nghỉ hoặc Học nghề của ĐÚNG lớp đang chọn — chỉ để hiện nhãn
  // tham khảo trên lưới, không dùng để ẩn/khoá ô nào. Hàng trong
  // vanhoaDistribution KHÔNG có classId, chỉ có className — phải so theo tên.
  const selectedClassName = classesById[classId];
  const selectedVanhoaRow = useMemo(
    () => vanhoaRows.find((r) => r.className === selectedClassName),
    [vanhoaRows, selectedClassName]
  );
  const dayTypeAt = (day, period) => {
    if (!selectedVanhoaRow || !selectedVanhoaRow.cells) return null;
    const cell = selectedVanhoaRow.cells[day];
    if (!cell) return null;
    const buoi = period <= 4 ? "sang" : "chieu";
    const t = cell[buoi];
    if (t === "nghi") return "Nghỉ";
    if (t === "nghe") return "Học nghề";
    if (t === "thiks") return "Thi KS";
    return null;
  };

  const isBusyAt = (tid, day, period) => {
    const busyMap = busyWeek[tid];
    if (!busyMap) return false;
    return Object.keys(busyMap).some((k) => busyKeyToPairs(k).some(([d, p]) => d === day && p === period));
  };
  // Khoá ĐÃ LƯU trên server của đúng GV đang xem, tại (day,period).
  const serverLockAt = (day, period) => (locks || []).find((l) => l.teacherId === teacherId && l.day === day && l.period === period);
  // Khoá HIỆN TẠI (đã áp cả thay đổi chưa lưu) — dùng để vẽ lưới.
  const effectiveLockAt = (day, period) => {
    const key = `${day}_${period}`;
    if (Object.prototype.hasOwnProperty.call(pending, key)) return pending[key]; // object mới | null (đã xoá)
    return serverLockAt(day, period) || null;
  };

  const dirtyKeys = Object.keys(pending);

  // Ghi chú của lớp đang chọn (tab "📝 Ghi chú") tại (day,period).
  const noteAt = (day, period) => (notes[classId] || {})[`${day}_${period}`] || null;
  const editNote = async (day, period) => {
    const current = noteAt(day, period);
    const text = window.prompt("Ghi chú cho ô này (để trống rồi OK để xoá ghi chú):", current ? current.note : "");
    if (text === null) return; // bấm Huỷ
    setNoteSaving(true);
    setError("");
    try {
      await setPeriodNote(schoolId, weekId, { classId, day, period, note: text, teacherId: noteTeacherId || (current && current.teacherId) || "" });
      const pn = await getPeriodNotes(schoolId, weekId);
      setNotes(pn || {});
    } catch (e) {
      setError(e.message);
    } finally {
      setNoteSaving(false);
    }
  };

  const toggleCell = (day, period) => {
    const key = `${day}_${period}`;
    const server = serverLockAt(day, period);
    const current = effectiveLockAt(day, period);
    setPending((prev) => {
      const next = { ...prev };
      if (current) {
        // Đang có khoá (của server hoặc mới thêm chưa lưu) -> bấm để BỎ.
        if (server) next[key] = null; // đánh dấu xoá khi lưu
        else delete next[key]; // huỷ luôn phần vừa thêm, chưa từng có trên server
      } else {
        // Chưa có khoá -> bấm để THÊM, dùng Lớp/Môn/Lý do đang chọn ở trên.
        next[key] = { classId, subjectId, reason };
      }
      return next;
    });
  };

  const saveChanges = async () => {
    setSaving(true);
    setError("");
    try {
      for (const key of dirtyKeys) {
        const [dayStr, periodStr] = key.split("_");
        const day = Number(dayStr), period = Number(periodStr);
        const value = pending[key];
        const server = serverLockAt(day, period);
        if (value === null && server) {
          await deleteManualLock(schoolId, weekId, server.id);
        } else if (value && !server) {
          await addManualLock(schoolId, weekId, { classId: value.classId, subjectId: value.subjectId, day, period, teacherId, reason: value.reason });
        }
      }
      reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };
  const cancelChanges = () => setPending({});

  if (!schoolId) {
    return <div style={{ padding: 16 }}>Chưa chọn trường. Vào <Link to="/happyschool/schools">Trường của tôi</Link>.</div>;
  }
  if (!weekId) return <p style={{ color: "#6b7280" }}>Chưa chọn tuần TKB.</p>;
  if (loading && locks === null) return <p>Đang tải...</p>;

  const title = `TKB GV: ${selectedTeacher?.name || ""}${selectedTeacher?.employmentType ? ` (${selectedTeacher.employmentType})` : ""}`;

  return (
    <div>
      {scope && (
        <p style={{ fontSize: 13, color: "#0f766e", background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 6, padding: "6px 10px", marginTop: 0 }}>
          {scope === "tg" ? "Chỉ hiện GV Thỉnh giảng" : "Chỉ hiện lớp khối 12 và các môn thi tốt nghiệp (Toán, Ngữ văn, Lịch sử, Địa lý, GDKT&PL, Công nghệ)"}
          {" "}— đang có <b>{scopedLocks.length}</b> ô xếp tay của {lockedTeacherIds.size} GV. Dùng chung dữ liệu với "Case xếp tay".
        </p>
      )}
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        <button type="button" onClick={() => setView("edit")} style={view === "edit" ? tabBtnActive : tabBtn}>🔒 Khoá ô</button>
        <button type="button" onClick={() => setView("browse")} style={view === "browse" ? tabBtnActive : tabBtn}>
          👀 TKB các GV được xếp tay{scopedLocks.length ? ` (${lockedTeacherIds.size})` : ""}
        </button>
        <button type="button" onClick={() => setView("notes")} style={view === "notes" ? tabBtnActive : tabBtn}>
          📝 Ghi chú
        </button>
      </div>

      {view === "browse" ? (
        <HappySchoolAllTeachersView runId={latestRunId} weekId={weekId} onlyTeacherIds={lockedTeacherIds} onlyLockedKeys={lockedCellKeys} locks={scope ? scopedLocks : locks} lockEdit onLocksChanged={reload} />
      ) : view === "notes" ? (
        <>
          <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
            Chọn Lớp, bấm vào ô muốn ghi chú (độc lập môn/GV đang xếp ở đó, không mất khi chạy lại CP-SAT) — vd để trống 1 tiết nhưng cần ghi "cô X trông lớp hộ".
          </p>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
            <select value={classId} onChange={(e) => setClassId(e.target.value)} style={sel}>
              {classes.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
            <select value={noteTeacherId} onChange={(e) => setNoteTeacherId(e.target.value)} style={sel} title="Gắn ghi chú với 1 GV để hiện trên TKB của GV đó (tuỳ chọn)">
              <option value="">— Không gắn GV —</option>
              {staff.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
            </select>
            {noteSaving && <span style={{ fontSize: 13, color: "#b45309" }}>Đang lưu...</span>}
          </div>
          {error && <p style={{ color: "#dc2626", fontSize: 13 }}>{error}</p>}
          <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 24 }}>
            <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ ...thFix(0), minWidth: 60, zIndex: 6 }}>Buổi</th>
                  <th style={{ ...thFix(60), minWidth: 56, zIndex: 6 }}>Tiết</th>
                  {DAYS.map((d) => (<th key={d} style={{ ...th, minWidth: 120 }}>{d}</th>))}
                </tr>
              </thead>
              <tbody>
                {SESSIONS.map((ses, si) =>
                  ses.periods.map((p, ti) => (
                    <tr key={`${si}-${ti}`}>
                      {ti === 0 && (
                        <td rowSpan={ses.periods.length} style={{ ...td, position: "sticky", left: 0, zIndex: 5, fontWeight: 700, background: "#f9fafb", verticalAlign: "middle", textTransform: "uppercase" }}>{ses.label}</td>
                      )}
                      <td style={{ ...td, position: "sticky", left: 60, zIndex: 5, background: "#fff", whiteSpace: "nowrap" }}>Tiết {ti + 1}</td>
                      {DAYS.map((d, day) => {
                        const n = noteAt(day, p);
                        return (
                          <td
                            key={d}
                            onClick={() => editNote(day, p)}
                            style={{ ...td, minWidth: 120, background: n ? "#fef9c3" : "#fff", cursor: "pointer" }}
                            title={n ? n.note : "Bấm để thêm ghi chú"}
                          >
                            {n ? <div style={{ color: "#92400e", whiteSpace: "pre-wrap" }}>📝 {n.note}{n.teacherId ? ` (${(staff.find((x) => x.id === n.teacherId) || {}).name || "GV"})` : ""}</div> : ""}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        Chọn GV, chọn Lớp/Môn muốn khoá, rồi bấm ô trong lưới để tích/bỏ — giống hệt trang "Lịch bận". Bấm "Lưu thay đổi" để áp dụng. (GV chưa có trong PCCM lớp-môn đó thì tự qua tab "Phân công chuyên môn" thêm trước, không thì khoá không có tác dụng khi xếp.) Ô màu xám ("Nghỉ") / vàng ("Học nghề") là ngày/buổi của lớp đang chọn — chỉ để tham khảo, vẫn bấm khoá đè lên bình thường.
      </p>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
        <select value={teacherId} onChange={(e) => setTeacherId(e.target.value)} style={sel}>
          {scopedStaff.map((s) => (<option key={s.id} value={s.id}>{s.name}{s.employmentType ? ` (${s.employmentType})` : ""}{scope && lockedTeacherIds.has(s.id) ? ` — ${scopedLocks.filter((l) => l.teacherId === s.id).length} ô` : ""}</option>))}
        </select>
        <select value={classId} onChange={(e) => setClassId(e.target.value)} style={sel}>
          {scopedClasses.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
        </select>
        <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)} style={sel}>
          {scopedSubjects.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
        </select>
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Lý do (tuỳ chọn)" style={{ ...sel, minWidth: 160 }} />
        <strong style={{ fontSize: 14 }}>{title}</strong>
        {dirtyKeys.length > 0 && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginLeft: "auto" }}>
            <span style={{ fontSize: 13, color: "#b45309", fontWeight: 600 }}>● {dirtyKeys.length} ô chưa lưu</span>
            <button type="button" onClick={cancelChanges} disabled={saving} style={sel}>Huỷ</button>
            <button type="button" onClick={saveChanges} disabled={saving} style={btn}>{saving ? "Đang lưu..." : "💾 Lưu thay đổi"}</button>
          </div>
        )}
      </div>
      {error && <p style={{ color: "#dc2626", fontSize: 13 }}>{error}</p>}

      <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 24 }}>
        <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
          <thead>
            <tr>
              <th style={{ ...thFix(0), minWidth: 60, zIndex: 6 }}>Buổi</th>
              <th style={{ ...thFix(60), minWidth: 56, zIndex: 6 }}>Tiết</th>
              {DAYS.map((d) => (<th key={d} style={{ ...th, minWidth: 120 }}>{d}</th>))}
            </tr>
          </thead>
          <tbody>
            {SESSIONS.map((ses, si) =>
              ses.periods.map((p, ti) => (
                <tr key={`${si}-${ti}`}>
                  {ti === 0 && (
                    <td rowSpan={ses.periods.length} style={{ ...td, position: "sticky", left: 0, zIndex: 5, fontWeight: 700, background: "#f9fafb", verticalAlign: "middle", textTransform: "uppercase" }}>{ses.label}</td>
                  )}
                  <td style={{ ...td, position: "sticky", left: 60, zIndex: 5, background: "#fff", whiteSpace: "nowrap" }}>Tiết {ti + 1}</td>
                  {DAYS.map((d, day) => {
                    const busy = isBusyAt(teacherId, day, p);
                    const lock = effectiveLockAt(day, p);
                    const dayType = dayTypeAt(day, p); // "Nghỉ" | "Học nghề" | null — chỉ tham khảo
                    const key = `${day}_${p}`;
                    const isDirty = Object.prototype.hasOwnProperty.call(pending, key);
                    let bg = "#fff", cellContent = null, tip = "Bấm để khoá";
                    const dayTypeTag = dayType && (
                      <div style={{ color: dayType === "Nghỉ" ? "#6b7280" : "#b45309", fontSize: 10, fontStyle: "italic" }}>{dayType}</div>
                    );
                    if (lock) {
                      // Đã khoá NHƯNG GV báo bận đúng giờ đó -> tô tím để cảnh báo,
                      // thay vì xanh bình thường (dễ bỏ sót đang đè lên lịch bận).
                      if (busy) {
                        bg = "#f3e8ff";
                        cellContent = (
                          <>
                            <div>{classesById[lock.classId] || lock.classId}</div>
                            <div style={{ color: "#7c3aed", fontSize: 11 }}>{subjectsById[lock.subjectId] || lock.subjectId} ⚠️ Bận</div>
                            {dayTypeTag}
                          </>
                        );
                        tip = "Đang khoá đè lên giờ GV báo bận — bấm để bỏ khoá";
                      } else {
                        bg = isDirty ? "#dbeafe" : "#eff6ff";
                        cellContent = (
                          <>
                            <div>{classesById[lock.classId] || lock.classId}</div>
                            <div style={{ color: "#6b7280", fontSize: 11 }}>{subjectsById[lock.subjectId] || lock.subjectId}</div>
                            {dayTypeTag}
                          </>
                        );
                        tip = "Bấm để bỏ khoá";
                      }
                    } else if (busy) {
                      bg = "#fee2e2";
                      cellContent = (
                        <>
                          <div style={{ color: "#dc2626", fontSize: 11 }}>Bận</div>
                          {dayTypeTag}
                        </>
                      );
                      tip = "GV báo bận — vẫn bấm khoá được, chỉ là cảnh báo";
                    } else if (dayType) {
                      bg = dayType === "Nghỉ" ? "#f3f4f6" : "#fef3c7";
                      cellContent = dayTypeTag;
                      tip = `Lớp đang ${dayType === "Nghỉ" ? "nghỉ" : "học nghề"} tiết này — vẫn bấm khoá đè lên được`;
                    }
                    return (
                      <td
                        key={d}
                        onClick={() => toggleCell(day, p)}
                        style={{ ...td, minWidth: 120, background: bg, cursor: "pointer" }}
                        title={tip}
                      >
                        {cellContent}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      </>
      )}
    </div>
  );
};

const sel = { padding: "6px 8px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13 };
const btn = { padding: "6px 12px", border: "1px solid #7c3aed", borderRadius: 6, fontSize: 13, background: "#7c3aed", color: "#fff", fontWeight: 600, cursor: "pointer" };
const tabBtn = { padding: "6px 14px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 999, cursor: "pointer", fontWeight: 600, fontSize: 13 };
const tabBtnActive = { ...tabBtn, background: "#7c3aed", color: "#fff", borderColor: "#7c3aed" };
const th = { border: "1px solid #e5e7eb", padding: "4px 6px", background: "#f9fafb", whiteSpace: "nowrap", textAlign: "center", position: "sticky" };
const thFix = (left) => ({ ...th, position: "sticky", left, top: 0, zIndex: 4, background: "#f3f4f6" });
const td = { border: "1px solid #e5e7eb", padding: "3px 5px", textAlign: "center", verticalAlign: "top" };

export default HappySchoolManualLocks;
